import moment from 'moment-timezone';
import { PhysicalQuantityPerMetric, isMetricWithUnit } from '../../enums/UnitChoices';
import { getConverters } from '../units';
const defaultNumberOfSignificantDigits = 1;
const numberOfSignificantDigits = {
    electricalConductivity: (unit) => {
        switch (unit) {
            case 'decisiemens_per_meter': {
                return 2;
            }
            default: {
                return defaultNumberOfSignificantDigits;
            }
        }
    },
    surfacePowerDensity: () => defaultNumberOfSignificantDigits,
    photosyntheticActiveRadiation: () => defaultNumberOfSignificantDigits,
    time: () => defaultNumberOfSignificantDigits,
    temperature: () => defaultNumberOfSignificantDigits,
    length: (unit) => {
        switch (unit) {
            case 'inches': {
                return 3;
            }
            default: {
                return defaultNumberOfSignificantDigits;
            }
        }
    },
    windSpeed: () => {
        return 0;
    },
    heatAccumulation: () => defaultNumberOfSignificantDigits,
    leafWetness: (unit) => {
        switch (unit) {
            case 'lws_intensity_level': {
                return 0;
            }
            default: {
                return defaultNumberOfSignificantDigits;
            }
        }
    },
    leafWetnessDuration: () => 0,
    degrees: () => defaultNumberOfSignificantDigits,
    pressure: () => defaultNumberOfSignificantDigits,
    percent: () => defaultNumberOfSignificantDigits,
    voltage: () => defaultNumberOfSignificantDigits,
    rainfallIntensity: (unit) => {
        switch (unit) {
            case 'inches_per_hour': {
                return 3;
            }
            default: {
                return defaultNumberOfSignificantDigits;
            }
        }
    },
    distance: () => 2
};
export const getNumberOfSignificantDigits = (metric) => {
    if (isMetricWithUnit(metric)) {
        return numberOfSignificantDigits[PhysicalQuantityPerMetric[metric]];
    }
    else {
        return () => defaultNumberOfSignificantDigits;
    }
};
const formatValueToStringOrNumber = ({ intl, value, metric, unit, displayUnit = false, abbreviatedUnit = true, asNumber = false, convertValue = true, showEmptyState = false, ...otherOptions }) => {
    const { t, formatNumber } = intl;
    const convertedValue = convertValue && isMetricWithUnit(metric)
        ? getConverters(metric)(value, unit)
        : value !== undefined
            ? value
            : null;
    if (!asNumber) {
        const options = {
            maximumFractionDigits: getNumberOfSignificantDigits(metric)(unit),
            unit: displayUnit ? unit : undefined,
            ...otherOptions
        };
        // special case when value is rainfalls in millimeters between 0 and 0.1
        // we want to display a string <0.1
        if (metric === 'RR' &&
            unit === 'millimeters' &&
            convertedValue != null &&
            convertedValue > 0 &&
            convertedValue < 0.1) {
            const longUnitMessageId = `${unit}`;
            const abbreviatedUnitMessageId = `${longUnitMessageId}_abbr`;
            const unitMessageId = abbreviatedUnit ? abbreviatedUnitMessageId : longUnitMessageId;
            const unitMessage = `units.${unitMessageId}`;
            const displayedUnit = displayUnit && unit && unitMessage && t(unitMessage);
            return displayedUnit ? `<0.1 ${displayedUnit}` : '<0.1';
        }
        return convertedValue !== null
            ? formatNumber(convertedValue, options)
            : showEmptyState
                ? formatNumber(undefined, options)
                : '';
    }
    else {
        return convertedValue;
    }
};
export const formatValue = (option) => formatValueToStringOrNumber({ ...option, asNumber: false });
export const formatAndConvertValueToNumber = (option) => formatValueToStringOrNumber({ ...option, asNumber: true, displayUnit: false });
export const formatValueToString = (option) => formatValueToStringOrNumber({
    ...option,
    asNumber: false,
    displayUnit: option.displayUnit ?? false,
    convertValue: false
});
export const formatMeasurementsForChart = (measurements, { timezone, formatDate, intl, metric, unit }) => measurements.map((datum) => {
    const timestamp = datum[0];
    const rawValue = datum[1];
    const value = formatAndConvertValueToNumber({
        intl,
        value: rawValue,
        metric,
        unit
    });
    const momentObject = moment.unix(timestamp).tz(timezone);
    return {
        x: momentObject.clone(),
        y: value,
        timestamp,
        value,
        formattedDate: formatDate(momentObject),
        formattedValue: formatValue({
            intl,
            value: rawValue,
            metric,
            unit,
            displayUnit: true
        })
    };
});
